import axios from 'axios';

// API Base URLs
const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Get user's geolocation information
 * @returns {Promise<Object>} Geolocation data
 */
export const getGeoLocation = async () => {
  try {
    const response = await axios.get(GEOLOCATION_API, {
      headers: {
        'accept': 'text/plain'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching geolocation:', error);
    return null;
  }
};

/**
 * Submit lead/contact form
 * @param {Object} leadData - Lead information
 * @param {string} leadData.name - Full name
 * @param {string} leadData.email - Email address
 * @param {number} leadData.countryDialCode - Country dial code (numeric only, e.g., 1, 44, 91)
 * @param {string} leadData.country - Country code (e.g., "US", "UK", "IN")
 * @param {string} leadData.phoneNumber - Phone number (numeric only, no country code prefix)
 * @param {string} leadData.ipAddress - IP address from geolocation
 * @param {string} leadData.description - Message/description
 * @param {string} leadData.pageUrl - Current page URL
 * @param {string} [leadData.city] - City from geolocation (optional)
 * @param {string} [leadData.state] - State from geolocation (optional)
 * @param {string} [leadData.geoLocation] - Stringified geolocation data (optional)
 * @param {string} [leadData.packageTitle] - Package title (optional)
 * @param {number} [leadData.packageAmount] - Package amount (optional)
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (leadData) => {
  try {
    const response = await axios.post(LEAD_API, leadData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return {
      success: true,
      data: response.data,
      statusCode: response.status
    };
  } catch (error) {
    if (error.response) {
      // Server responded with error status
      return {
        success: false,
        data: error.response.data,
        statusCode: error.response.status,
        message: error.response.data?.message || error.response.data?.errorMessage || 'An error occurred'
      };
    } else {
      // Network or other error
      return {
        success: false,
        statusCode: 500,
        message: 'Network error. Please try again.'
      };
    }
  }
};

/**
 * Helper function to extract numeric phone number from formatted string
 * @param {string} phoneString - Phone string (e.g., "+1 555-123-4567" or "5551234567")
 * @returns {string} Numeric only phone number
 */
export const extractPhoneNumber = (phoneString) => {
  if (!phoneString) return '';
  // Remove all non-numeric characters
  return phoneString.replace(/\D/g, '');
};

/**
 * Helper function to extract country dial code from phone string
 * @param {string} phoneString - Phone string (e.g., "+1 555-123-4567")
 * @returns {string} Country dial code without + sign
 */
export const extractDialCode = (phoneString) => {
  if (!phoneString) return '1';
  const match = phoneString.match(/^\+?(\d+)/);
  return match ? match[1] : '1';
};

/**
 * Map country code from geolocation to dial code
 * @param {string} countryCode - ISO country code (e.g., "US", "UK", "IN")
 * @returns {number} Country dial code
 */
export const getDialCodeFromCountryCode = (countryCode) => {
  const dialCodeMap = {
    'US': 1, 'CA': 1,
    'GB': 44, 'UK': 44,
    'IN': 91,
    'CN': 86,
    'JP': 81,
    'DE': 49,
    'FR': 33,
    'AU': 61,
    'BR': 55,
    'MX': 52,
    'IT': 39,
    'ES': 34,
    'RU': 7,
    'KR': 82,
    'NL': 31,
    'SE': 46,
    'CH': 41,
    'SG': 65,
    'AE': 971,
    'ZA': 27,
    'PK': 92,
    'BD': 880,
    'NG': 234,
    'EG': 20,
    'TR': 90,
    'TH': 66,
    'VN': 84,
    'PH': 63,
    'MY': 60,
    'ID': 62,
    'SA': 966,
    'IL': 972,
    'NZ': 64,
    'AR': 54,
    'CO': 57,
    'CL': 56,
    'PE': 51,
    'VE': 58,
    'PL': 48,
    'UA': 380,
    'RO': 40,
    'GR': 30,
    'PT': 351,
    'BE': 32,
    'AT': 43,
    'NO': 47,
    'DK': 45,
    'FI': 358,
    'IE': 353,
    'CZ': 420,
    'HU': 36
  };
  
  return dialCodeMap[countryCode] || 1;
};
